import { z } from 'zod';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [LIFECYCLE] DOMAIN EVENTS — WHAT HAPPENED, AND TO WHOM AS IDENTITIES.
 *
 *  PA_REQUIREMENTS Appendix C names "PA cancelled → all assigned officers + nodals" as an
 *  SMS trigger, and FR-5.6.1 puts the OUTBOX — queued, retried, status-tracked — in P6.
 *  This pass emits the event; it does not send anything and does not schedule anything.
 *
 *  ⚠️ WHAT STOPS THIS FROM BEING THE OUTBOX IT DEFERS: THERE IS NO COLUMN A DISPATCHER
 *  COULD CLAIM, AND NO INDEX A SWEEP COULD USE.
 *
 *  An outbox is a DISPATCH STATE MACHINE. It is defined by `status`, `attempts`,
 *  `next_attempt_at`, `locked_by`/`claimed_at`, `sent_at`, a channel, a DLT template id, a
 *  rendered body and a recipient ADDRESS. This schema declares NONE of them, and
 *  `domainEvent.model.ts` declares no index beyond `_id`. Nothing can drain it by accident
 *  — and, more to the point, nothing can HALF-drain it, because there is no column in
 *  which a partial drain could be recorded.
 *
 *  It records what happened and to whom as IDENTITIES. It cannot record whether anybody
 *  was told. `api/tests/integration/outboxDeferred.test.ts` asserts every clause of that
 *  paragraph against the source, and carries P6's shopping list in its header.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ WRITTEN OUT, AND A NEW MEMBER IS A DECISION TAKEN HERE.
 *
 * Same rule and same reason as `ARRANGEMENT_STATUSES_CARRYING_WORK`: an event type is a
 * promise that something downstream will eventually act on it, so widening this tuple
 * reaches into P6's work rather than merely adding a string.
 */
export const DOMAIN_EVENT_TYPES = [
  'ARRANGEMENT_CANCELLED',
  /*
   * ⚠️ [D4.4 §5] "THIS ORDER HAS BEEN TAKEN BACK TO BE CHANGED."
   *
   * Emitted when a PUBLISHED order is returned to DRAFT while officers were already named.
   * Its recipients are the same set an archive uses — every officer holding an ASSIGNED
   * slot, and every addressed org unit — because they are the people who were told
   * something that has stopped being true.
   *
   * ⚠️ IT IS NOT `ARRANGEMENT_CANCELLED`, AND REUSING THAT WOULD BE A LIE TO P6. A
   * cancellation says "this duty is off"; a withdrawal says "this duty is being reworked
   * and you will hear again". P6 will render different words, and a dispatcher cannot
   * recover the difference from a `context` key — which is exactly why this tuple is
   * written out and widening it is a DECISION taken here rather than a string added.
   *
   * ⚠️ NOTHING IS SENT. Same as its sibling: this table records what happened and to whom
   * as identities, and has no column in which a dispatch could be recorded.
   * `outboxDeferred.test.ts` asserts that clause by clause, and its tuple assertion was
   * WIDENED in the same commit that added this member rather than deleted.
   */
  'ARRANGEMENT_WITHDRAWN',
] as const;
export type DomainEventType = (typeof DOMAIN_EVENT_TYPES)[number];

export const DOMAIN_EVENT_SUBJECT_TYPES = ['ARRANGEMENT'] as const;
export type DomainEventSubjectType = (typeof DOMAIN_EVENT_SUBJECT_TYPES)[number];

export const domainEventSchema = z.object({
  id: objectIdSchema,
  eventType: z.enum(DOMAIN_EVENT_TYPES),
  /** The business instant, not the write instant. */
  occurredAt: z.iso.datetime(),
  subjectType: z.enum(DOMAIN_EVENT_SUBJECT_TYPES),
  subjectId: objectIdSchema,
  actorOfficerId: objectIdSchema,
  actorOrgUnitId: objectIdSchema,
  /**
   * ⚠️ DISTINCT OFFICERS, AND ONE EVENT CARRYING THE WHOLE SET — never one event per
   * recipient. FR-5.6.3 requires de-duplication per officer per PA per hour, and N rows
   * would make that a dispatcher's problem to solve later; one row makes it a property of
   * the SHAPE. A 500-duty order would also be thousands of inserts inside a transaction
   * the server gives sixty seconds.
   */
  recipientOfficerIds: z.array(objectIdSchema),
  /**
   * ⚠️ THE NODAL HALF IS AN ORG UNIT, NEVER AN OFFICER. "Who is the nodal of Central
   * Division" is a directory question whose answer changes between the archive and the
   * send; storing the unit lets P6 resolve the current holder at send time, while storing
   * a person sends a cancellation to the wrong desk after a transfer.
   */
  recipientOrgUnitIds: z.array(objectIdSchema),
  /**
   * ⚠️ NO PHONE NUMBER, EVER. The event carries identities; P6 looks the number up from
   * the officer master, which is where FR-5.8.3's normalisation lives. A copy here would
   * be a second copy of PII with nothing keeping it honest.
   */
  context: z.record(z.string(), z.string()),
  createdAt: z.iso.datetime(),
});
